/**
 * depthProgress.js — shared depth-band color/label logic and depth-ring
 * SVG rendering for the Discovery Progress tab's polar-map redesign.
 *
 * Consolidates the depth-band thresholds previously duplicated across
 * `DiscoverySection._computeMetaStats` / `_getOverallLevelLabel` — those call
 * sites should import from here instead of re-deriving the bands. Only the
 * band thresholds + labels are consolidated; `_computeMetaStats`'s
 * section-weighted `overallPercent` math is a separate, retired concern (the
 * `/progress/core` API's `overall` is now the sole source for that number).
 *
 * Dependency-free — safe to import anywhere in frontend-user.
 *
 *   import { DEPTH_BANDS, levelOf, levelLabel } from '@/components/depthProgress.js';
 *   levelOf(72);                                    // 'deep'
 *   levelLabel(72);                                  // 'Deep'
 */

/**
 * Band fill colors keyed by band id. Sequential single-hue ramp — gray
 * (not started) stepping through three shades of blue to the deepest band.
 * @type {{none: string, beginning: string, intermediate: string, deep: string}}
 */
export const DEPTH_BANDS = {
  none: '#d9e0e7',
  beginning: '#aac8db',
  intermediate: '#639bb8',
  deep: '#2f5c85'
};

const LEVEL_LABELS = {
  none: 'Not started',
  beginning: 'Beginning',
  intermediate: 'Intermediate',
  deep: 'Deep'
};

/**
 * Clamp a progress value to [0, 100], coercing non-finite input (NaN,
 * undefined, null, Infinity, non-numeric strings) to 0.
 * @param {number} progress - Raw progress value
 * @returns {number} Clamped 0-100 value
 */
function clampProgress(progress) {
  const n = Number(progress);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/**
 * Map a 0-100 progress value to its depth-band key.
 * Thresholds: Deep >=67, Intermediate >=34, Beginning >=1, else none.
 * @param {number} progress - Progress value 0-100 (clamped internally)
 * @returns {'none'|'beginning'|'intermediate'|'deep'} Band key
 */
export function levelOf(progress) {
  const v = clampProgress(progress);
  if (v >= 67) return 'deep';
  if (v >= 34) return 'intermediate';
  if (v >= 1) return 'beginning';
  return 'none';
}

/**
 * Map a 0-100 progress value to its display label.
 * @param {number} progress - Progress value 0-100 (clamped internally)
 * @returns {'Not started'|'Beginning'|'Intermediate'|'Deep'} Display word
 */
export function levelLabel(progress) {
  return LEVEL_LABELS[levelOf(progress)];
}

/** How many stepper rungs each band has reached. */
const BAND_RUNGS = { none: 0, beginning: 1, intermediate: 2, deep: 3 };

/** Total rungs — the three named bands above "not started". */
export const DEPTH_RUNGS = 3;

/**
 * Render the depth stepper: three rising bars, filled up to the band this
 * progress value has reached.
 *
 * The band word beside it says where you are; the stepper is what makes that
 * word ORDINAL and BOUNDED — "Beginning" alone cannot say there are three
 * rungs and two still to go. "Not started" is three empty bars, which reads
 * correctly rather than as a missing graphic.
 *
 * `aria-hidden`, deliberately: every surface that draws this puts the band
 * word in text right beside it, so announcing it again would only stutter.
 *
 * @param {number} progress - Progress value 0-100 (clamped internally)
 * @param {Object} [opts]
 * @param {string} [opts.color] - Fill for the reached rungs; defaults to the
 *   band ramp's deep tone. Surfaces with a topic identity pass its hue.
 * @returns {string} Self-contained HTML markup string
 */
export function renderDepthStepper(progress, { color } = {}) {
  const reached = BAND_RUNGS[levelOf(progress)] ?? 0;
  const fill = color || DEPTH_BANDS.deep;
  const bars = Array.from({ length: DEPTH_RUNGS }, (_, i) => {
    const on = i < reached;
    const style = on ? ` style="background:${fill}"` : '';
    return `<span class="depth-stepper__bar depth-stepper__bar--${i + 1}${on ? ' depth-stepper__bar--on' : ''}"${style}></span>`;
  }).join('');
  return `<span class="depth-stepper" aria-hidden="true">${bars}</span>`;
}
